import React, { Fragment, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { message, Button, Modal, Input } from "antd";
import { isAuth } from "../../auth/ProtectedRoute";
import { CircularProgress, Box } from "@mui/material";

interface SubmittedBy {
  id: number;
  fullname?: string;
  name?: string;
  email: string;
  phone?: string;
  image?: string;
  profile_image?: string;
}

interface Proof {
  id: number;
  proof_type: string;
  original_filename?: string;
  filename?: string;
  file_name?: string;
  file_path?: string;
  file?: string;
  path?: string;
  mime_type?: string;
  file_size?: number;
  has_file: boolean;
}

interface VerificationRequest {
  id: number;
  page_id: number;
  status: string;
  submitted_at: string;
  submitted_by: SubmittedBy;
  proofs: Proof[];
}

interface VerificationDetails {
  id: number;
  name: string;
  link: string;
  description: string;
  industry: string;
  size: string;
  logo: string;
  image: string;
  verification_status: string;
  official_website: string;
  official_email_domain: string;
  cac_registration_number: string | null;
  verification_submitted_at: string;
  verification_requests?: VerificationRequest[];
}

const ViewVerification = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [data, setData] = useState<VerificationDetails | null>(null);
  const [selectedProof, setSelectedProof] = useState<Proof | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [rejectModalVisible, setRejectModalVisible] = useState<boolean>(false);
  const [infoModalVisible, setInfoModalVisible] = useState<boolean>(false);
  const [approveModalVisible, setApproveModalVisible] = useState<boolean>(false);
  const [reviewNote, setReviewNote] = useState<string>("");
  const [imageError, setImageError] = useState<boolean>(false);

  useEffect(() => {
    setImageError(false);
  }, [selectedProof]);

  useEffect(() => {
    if (id) {
      fetchDetails();
    }
  }, [id]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await axios({
        method: "GET",
        url: `${process.env.REACT_APP_HOST_API}organisation-verifications/${id}`,
        headers: {
          Authorization: `Bearer ${isAuth()}`,
        },
      });

      if (response.data && response.data.response) {
        const pageData = response.data.data.page;
        setData(pageData);
        // Default to first proof with a file
        const request = pageData.verification_requests?.[0];
        const firstFileProof = request?.proofs?.find((p: any) => p.has_file);
        if (firstFileProof) {
          setSelectedProof(firstFileProof);
        } else if (request?.proofs?.length > 0) {
          setSelectedProof(request.proofs[0]);
        }
      } else {
        message.error("Failed to fetch verification request details");
        navigate("/admin/content-management/pages/verify-page");
      }
    } catch (error) {
      console.error("Error fetching verification details:", error);
      message.error("Error loading verification details");
      navigate("/admin/content-management/pages/verify-page");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveSubmit = async () => {
    if (!reviewNote.trim()) {
      message.error("Please provide a reason for approval.");
      return;
    }
    setSubmitting(true);
    try {
      await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST_API}organisation-verifications/${id}/approve`,
        headers: {
          Authorization: `Bearer ${isAuth()}`,
        },
        data: {
          review_note: reviewNote,
        },
      });
      message.success("Verification request approved successfully");
      setApproveModalVisible(false);
      setReviewNote("");
      fetchDetails();
    } catch (error) {
      console.error("Error approving request:", error);
      message.error("Failed to approve request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await axios({
        method: "DELETE",
        url: `${process.env.REACT_APP_HOST_API}organisation-verifications/${id}`,
        headers: {
          Authorization: `Bearer ${isAuth()}`,
        },
      });
      message.success("Verification request deleted successfully");
      navigate("/admin/content-management/pages/verify-page");
    } catch (error) {
      console.error("Error deleting request:", error);
      message.error("Failed to delete request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!reviewNote.trim()) {
      message.error("Please provide a reason for rejection.");
      return;
    }
    setSubmitting(true);
    try {
      await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST_API}organisation-verifications/${id}/reject`,
        headers: {
          Authorization: `Bearer ${isAuth()}`,
        },
        data: {
          review_note: reviewNote,
        },
      });
      message.success("Verification request rejected successfully");
      setRejectModalVisible(false);
      setReviewNote("");
      fetchDetails();
    } catch (error) {
      console.error("Error rejecting request:", error);
      message.error("Failed to reject request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestMoreInfoSubmit = async () => {
    if (!reviewNote.trim()) {
      message.error("Please specify what additional information is needed.");
      return;
    }
    setSubmitting(true);
    try {
      await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST_API}organisation-verifications/${id}/request-more-info`,
        headers: {
          Authorization: `Bearer ${isAuth()}`,
        },
        data: {
          review_note: reviewNote,
        },
      });
      message.success("Information request sent successfully");
      setInfoModalVisible(false);
      setReviewNote("");
      fetchDetails();
    } catch (error) {
      console.error("Error requesting additional info:", error);
      message.error("Failed to send information request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    try {
      const normalized = dateStr.replace(" ", "T");
      const d = new Date(normalized);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
        <CircularProgress size={50} thickness={4} style={{ color: "#0d6efd" }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh", flexDirection: "column", gap: 2 }}>
        <h3>No verification details found.</h3>
        <Button type="primary" onClick={() => navigate("/admin/content-management/pages/verify-page")}>
          Back to Verification List
        </Button>
      </Box>
    );
  }

  const request = data.verification_requests?.[0];
  const submittedBy = request?.submitted_by;
  const submittedByName = submittedBy?.fullname || submittedBy?.name || "N/A";
  const submittedByEmail = submittedBy?.email || "N/A";
  const userAvatar = submittedBy?.image || submittedBy?.profile_image || "";
  const proofs = request?.proofs || [];

  const getProofFileUrl = (proof: Proof) => {
    if (!proof) return "";

    const candidateFields = [
      proof.file_path,
      proof.file_name,
      proof.filename,
      proof.file,
      proof.path,
      proof.original_filename
    ];

    let pathOrUrl = "";
    for (const field of candidateFields) {
      if (field && typeof field === "string") {
        pathOrUrl = field.trim();
        break;
      }
    }

    if (!pathOrUrl) return "";

    if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) {
      return pathOrUrl;
    }

    const prefix = process.env.REACT_APP_IMAGE_API_PREFIX || "";
    const cleanPrefix = prefix.endsWith("/") ? prefix : `${prefix}/`;
    const cleanPath = pathOrUrl.startsWith("/") ? pathOrUrl.substring(1) : pathOrUrl;

    let url = `${cleanPrefix}${cleanPath}`;

    if (
      cleanPrefix.includes("blob.core.windows.net") &&
      !cleanPrefix.includes("blob.core.windows.net/kominitifiles") &&
      !cleanPath.startsWith("kominitifiles/")
    ) {
      url = `${cleanPrefix}kominitifiles/${cleanPath}`;
    }

    return url;
  };

  const renderPreview = () => {
    if (!selectedProof) {
      return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "#6c757d", flexDirection: "column", gap: "10px" }}>
          <i className="fa fa-file-text-o" style={{ fontSize: "48px" }}></i>
          <span>No proof document selected</span>
        </div>
      );
    }

    if (!selectedProof.has_file) {
      return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "#6c757d", flexDirection: "column", gap: "12px", padding: "40px", textAlign: "center" }}>
          <i className="fa fa-envelope-o" style={{ fontSize: "56px", color: "#0d6efd" }}></i>
          <h3 style={{ margin: "10px 0 0 0", fontSize: "16px", fontWeight: 600 }}>Domain Verification Proof</h3>
          <p style={{ margin: "6px 0 0 0", fontSize: "13.5px", color: "#868e96", lineHeight: "1.5" }}>
            This type of verification does not require an uploaded document. It relies on the requesting user matching the organization&apos;s official email domain.
          </p>
        </div>
      );
    }

    const fileUrl = getProofFileUrl(selectedProof);

    const checkIsExtension = (ext: string) => {
      const targets = [
        selectedProof.original_filename,
        selectedProof.filename,
        selectedProof.file_name,
        selectedProof.file_path,
        selectedProof.file,
        selectedProof.path
      ];
      return targets.some(val => {
        if (!val) return false;
        const basePath = val.split("?")[0].toLowerCase();
        return basePath.endsWith(ext);
      });
    };

    const isImage = 
      checkIsExtension(".png") || checkIsExtension(".jpg") || checkIsExtension(".jpeg") || 
      checkIsExtension(".gif") || checkIsExtension(".webp") ||
      selectedProof.mime_type?.startsWith("image/");

    const isPdf = checkIsExtension(".pdf") || selectedProof.mime_type === "application/pdf";

    if (isImage) {
      if (imageError) {
        return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "#212529", flexDirection: "column", gap: "12px", padding: "40px", textAlign: "center" }}>
            <i className="fa fa-picture-o" style={{ fontSize: "56px", color: "#fa5252" }}></i>
            <h3 style={{ margin: 0, fontSize: "16px" }}>{selectedProof.original_filename || "Image Proof"}</h3>
            <p style={{ margin: 0, fontSize: "13.5px", color: "#868e96" }}>
              Unable to load the image preview. This could be due to a private storage setting or network error.
            </p>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: "#0d6efd",
                color: "#fff",
                padding: "8px 16px",
                borderRadius: "6px",
                fontWeight: 600,
                textDecoration: "none",
                marginTop: "10px",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <i className="fa fa-download"></i> Download Image
            </a>
          </div>
        );
      }

      return (
        <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", overflow: "auto", padding: "20px" }}>
          <img
            src={fileUrl}
            alt="Proof preview"
            onError={() => setImageError(true)}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              transform: `scale(${zoom})`,
              transition: "transform 0.2s ease",
            }}
          />
        </div>
      );
    }

    if (isPdf) {
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
          <iframe
            src={`${fileUrl}#toolbar=0`}
            title="PDF preview"
            style={{ width: "100%", height: "100%", border: "none", flex: 1 }}
          />
          <div style={{ padding: "8px 16px", background: "#f1f3f5", borderTop: "1px solid #dee2e6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12px", color: "#495057" }}>
              PDF preview not loading?
            </span>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: "12px", color: "#0d6efd", fontWeight: 600, textDecoration: "underline" }}
            >
              Open PDF in a new tab
            </a>
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "#212529", flexDirection: "column", gap: "12px", padding: "40px", textAlign: "center" }}>
        <i className="fa fa-file-word-o" style={{ fontSize: "56px", color: "#228be6" }}></i>
        <h3 style={{ margin: 0, fontSize: "16px" }}>{selectedProof.original_filename || "Document Proof"}</h3>
        <p style={{ margin: 0, fontSize: "13.5px", color: "#868e96" }}>
          This file format ({selectedProof.mime_type || "document"}) cannot be previewed directly.
        </p>
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            background: "#0d6efd",
            color: "#fff",
            padding: "8px 16px",
            borderRadius: "6px",
            fontWeight: 600,
            textDecoration: "none",
            marginTop: "10px",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <i className="fa fa-download"></i> Download Document
        </a>
      </div>
    );
  };

  return (
    <Fragment>
      <div style={{ padding: "40px", minHeight: "100vh", background: "#fdfdfd" }}>
        {/* Navigation Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "28px" }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              background: "transparent",
              color: "#495057",
              border: "1px solid #dee2e6",
              borderRadius: "6px",
              padding: "6px 14px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "13px",
              transition: "all 0.2s"
            }}
          >
            <i className="fa fa-chevron-left" style={{ fontSize: "11px" }}></i>
            Back
          </button>
        </div>

        {/* Main Grid Wrapper */}
        <div style={{ display: "flex", gap: "28px", alignItems: "stretch" }}>
          {/* Left Column - Meta and Details */}
          <div style={{ width: "360px", display: "flex", flexDirection: "column", gap: "20px", flexShrink: 0 }}>
            {/* Title Block */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#212529" }}>
                  Verification Request
                </h2>
                <span style={{
                  background: data.verification_status === "pending_review" ? "#fff3cd" : "#d1e7dd",
                  color: data.verification_status === "pending_review" ? "#856404" : "#0f5132",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "capitalize"
                }}>
                  {data.verification_status === "pending_review" || data.verification_status === "requires_more_info" ? "Pending" : data.verification_status === "verified" ? "Verified" : "Approved"}
                </span>
              </div>
              <p style={{ margin: "4px 0 0 0", color: "#868e96", fontSize: "12.5px" }}>
                Submitted {formatDate(data.verification_submitted_at)}
              </p>
            </div>

            {/* User Profile Info */}
            <div style={{ background: "#f8f9fa", borderRadius: "10px", padding: "16px", border: "1px solid #e9ecef" }}>
              <h3 style={{ margin: "0 0 12px 0", fontSize: "13px", fontWeight: 700, color: "#868e96", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                User Profile
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {userAvatar ? (
                  <img
                    src={userAvatar.startsWith("http") ? userAvatar : `${process.env.REACT_APP_IMAGE_API_PREFIX || ""}${userAvatar}`}
                    style={{ width: "44px", height: "44px", borderRadius: "50%", objectFit: "cover" }}
                    alt="avatar"
                  />
                ) : (
                  <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: "#e9ecef", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "16px", fontWeight: 700, color: "#495057" }}>
                    {submittedByName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <strong style={{ fontSize: "14.5px", color: "#212529" }}>{submittedByName}</strong>
                  <span style={{ fontSize: "12.5px", color: "#495057", display: "flex", alignItems: "center", gap: "6px" }}>
                    <i className="fa fa-envelope-o" style={{ color: "#adb5bd" }}></i> 
                    <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: "230px" }}>{submittedByEmail}</span>
                  </span>
                  {submittedBy?.phone && (
                    <span style={{ fontSize: "12.5px", color: "#495057", display: "flex", alignItems: "center", gap: "6px" }}>
                      <i className="fa fa-phone" style={{ color: "#adb5bd" }}></i> {submittedBy.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Organization Info */}
            <div style={{ background: "#f8f9fa", borderRadius: "10px", padding: "16px", border: "1px solid #e9ecef" }}>
              <h3 style={{ margin: "0 0 12px 0", fontSize: "13px", fontWeight: 700, color: "#868e96", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                Organization Details
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                {data.logo ? (
                  <img
                    src={`${process.env.REACT_APP_IMAGE_API_PREFIX || ""}${data.logo}`}
                    style={{ width: "40px", height: "40px", borderRadius: "8px", objectFit: "cover", border: "1px solid #dee2e6" }}
                    alt="logo"
                  />
                ) : (
                  <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#0d6efd", display: "flex", justifyContent: "center", alignItems: "center", color: "#fff", fontSize: "16px", fontWeight: 700 }}>
                    {data.name?.charAt(0)}
                  </div>
                )}
                <div>
                  <strong style={{ fontSize: "14.5px", color: "#212529", display: "flex", alignItems: "center", gap: "4px" }}>
                    {data.name}
                    {(data.verification_status === "Approved" || data.verification_status === "verified") && (
                      <i className="fa fa-check-circle" style={{ color: "#0d6efd" }}></i>
                    )}
                  </strong>
                  <span style={{ fontSize: "12px", color: "#868e96" }}>{data.industry || "Industry"}</span>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "12.5px", borderTop: "1px solid #e9ecef", paddingTop: "12px" }}>
                <div>
                  <span style={{ color: "#adb5bd", display: "block", fontSize: "11px", fontWeight: 600 }}>WEBSITE</span>
                  {data.official_website ? (
                    <a href={data.official_website} target="_blank" rel="noopener noreferrer" style={{ color: "#0d6efd", textDecoration: "none", display: "flex", alignItems: "center", gap: "4px" }}>
                      {data.official_website} <i className="fa fa-external-link" style={{ fontSize: "10px" }}></i>
                    </a>
                  ) : "N/A"}
                </div>
                <div>
                  <span style={{ color: "#adb5bd", display: "block", fontSize: "11px", fontWeight: 600 }}>DOMAIN VERIFIED</span>
                  <strong style={{ color: "#212529" }}>{data.official_email_domain || "N/A"}</strong>
                </div>
              </div>
            </div>

            {/* Proofs Selector List */}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <h3 style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: "#868e96", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                Verification Proofs
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {proofs.map((proof, idx) => {
                  const isSelected = selectedProof?.id === proof.id;
                  const isBusinessReg = proof.proof_type === "business_registration";
                  const isOfficialEmail = proof.proof_type === "official_email_domain";
                  const proofTitle = isBusinessReg ? "Business License" : isOfficialEmail ? "Official Email Domain" : proof.proof_type;
                  const fileExt = proof.original_filename ? proof.original_filename.split(".").pop()?.toUpperCase() : "Document";

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        setSelectedProof(proof);
                        setZoom(1);
                      }}
                      style={{
                        background: isSelected ? "#e7f5ff" : "#fff",
                        border: isSelected ? "2px solid #0d6efd" : "1px solid #dee2e6",
                        borderRadius: "10px",
                        padding: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        transition: "all 0.2s"
                      }}
                    >
                      <div style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "6px",
                        background: isSelected ? "#d0ebff" : "#f1f3f5",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                      }}>
                        <i className={`fa ${proof.has_file ? "fa-file-text-o" : "fa-envelope-o"}`} style={{ color: "#0d6efd", fontSize: "18px" }}></i>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong style={{ fontSize: "13px", color: "#212529" }}>{proofTitle}</strong>
                        <span style={{ fontSize: "11px", color: "#868e96" }}>{proof.has_file ? fileExt : "Email Domain"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column - Document Preview Panel */}
          <div style={{ flex: 1, background: "#f8f9fa", borderRadius: "12px", border: "1px solid #dee2e6", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Toolbar */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px", borderBottom: "1px solid #dee2e6", background: "#fff" }}>
              <strong style={{ fontSize: "14px", color: "#495057" }}>Proof Preview</strong>
              {selectedProof?.has_file && (
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => setZoom(prev => Math.min(prev + 0.2, 3))}
                    style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "6px", width: "32px", height: "32px", cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center", outline: "none" }}
                    title="Zoom In"
                  >
                    <i className="fa fa-search-plus" style={{ color: "#495057" }}></i>
                  </button>
                  <button
                    onClick={() => setZoom(prev => Math.max(prev - 0.2, 0.5))}
                    style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "6px", width: "32px", height: "32px", cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center", outline: "none" }}
                    title="Zoom Out"
                  >
                    <i className="fa fa-search-minus" style={{ color: "#495057" }}></i>
                  </button>
                  <a
                    href={getProofFileUrl(selectedProof)}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "6px", width: "32px", height: "32px", cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center", textDecoration: "none" }}
                    title="Download"
                  >
                    <i className="fa fa-download" style={{ color: "#495057" }}></i>
                  </a>
                </div>
              )}
            </div>

            {/* Preview Area */}
            <div style={{ flex: 1, minHeight: "450px", position: "relative", background: "#e9ecef" }}>
              {renderPreview()}
            </div>
          </div>
        </div>

        {/* Sticky/Fixed Footer Action Buttons */}
        {data.verification_status !== "Approved" && data.verification_status !== "verified" && (
          <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "32px", padding: "20px 0", borderTop: "1px solid #dee2e6" }}>
            <Button
              style={{ borderRadius: "6px", height: "40px", padding: "0 24px", fontWeight: 600, border: "1px solid #dee2e6" }}
              onClick={() => {
                setReviewNote("");
                setInfoModalVisible(true);
              }}
            >
              Request Additional Proof
            </Button>
            <Button
              type="primary"
              danger
              style={{ borderRadius: "6px", height: "40px", padding: "0 24px", fontWeight: 600, background: "#fff5f5", color: "#ff6b6b", border: "1px solid #ffc9c9" }}
              onClick={() => {
                setReviewNote("");
                setRejectModalVisible(true);
              }}
            >
              Reject
            </Button>
            <Button
              type="primary"
              disabled={submitting}
              style={{ background: "#3b82f6", borderColor: "#3b82f6", borderRadius: "6px", height: "40px", padding: "0 28px", fontWeight: 600 }}
              onClick={() => {
                setReviewNote("");
                setApproveModalVisible(true);
              }}
            >
              Approve
            </Button>
          </div>
        )}

        {/* Request Additional Proof Modal */}
        <Modal
          title={null}
          visible={infoModalVisible}
          onCancel={() => {
            setInfoModalVisible(false);
            setReviewNote("");
          }}
          footer={null}
          closable={true}
          destroyOnClose={true}
          bodyStyle={{ padding: "24px" }}
          style={{ borderRadius: "12px", overflow: "hidden" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#212529" }}>Request Additional Proof</h2>
          </div>
          <p style={{ color: "#495057", fontSize: "14px", marginBottom: "16px" }}>
            Specify what additional information or documentation is needed from the user.
          </p>
          <Input.TextArea
            rows={4}
            placeholder="Please provide..."
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            style={{
              background: "#f1f3f5",
              border: "none",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "14px",
              color: "#495057",
              resize: "none",
              outline: "none",
              marginBottom: "24px"
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <Button
              onClick={() => {
                setInfoModalVisible(false);
                setReviewNote("");
              }}
              style={{
                borderRadius: "8px",
                height: "40px",
                padding: "0 20px",
                fontWeight: 600,
                border: "1px solid #dee2e6"
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={submitting}
              onClick={handleRequestMoreInfoSubmit}
              style={{
                borderRadius: "8px",
                height: "40px",
                padding: "0 20px",
                fontWeight: 600,
                background: "#3b82f6",
                borderColor: "#3b82f6"
              }}
            >
              Send Request
            </Button>
          </div>
        </Modal>

        {/* Reject Modal */}
        <Modal
          title={null}
          visible={rejectModalVisible}
          onCancel={() => {
            setRejectModalVisible(false);
            setReviewNote("");
          }}
          footer={null}
          closable={true}
          destroyOnClose={true}
          bodyStyle={{ padding: "24px" }}
          style={{ borderRadius: "12px", overflow: "hidden" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#212529" }}>Reject Verification Request</h2>
          </div>
          <p style={{ color: "#495057", fontSize: "14px", marginBottom: "16px" }}>
            Please provide a reason for rejecting this verification request. This will be sent to the user.
          </p>
          <Input.TextArea
            rows={4}
            placeholder="Reason for rejection..."
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            style={{
              background: "#f1f3f5",
              border: "none",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "14px",
              color: "#495057",
              resize: "none",
              outline: "none",
              marginBottom: "24px"
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <Button
              onClick={() => {
                setRejectModalVisible(false);
                setReviewNote("");
              }}
              style={{
                borderRadius: "8px",
                height: "40px",
                padding: "0 20px",
                fontWeight: 600,
                border: "1px solid #dee2e6"
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              loading={submitting}
              onClick={handleRejectSubmit}
              style={{
                borderRadius: "8px",
                height: "40px",
                padding: "0 20px",
                fontWeight: 600,
                background: "#ff3333",
                borderColor: "#ff3333"
              }}
            >
              Reject Request
            </Button>
          </div>
        </Modal>

        {/* Approve Modal */}
        <Modal
          title={null}
          visible={approveModalVisible}
          onCancel={() => {
            setApproveModalVisible(false);
            setReviewNote("");
          }}
          footer={null}
          closable={true}
          destroyOnClose={true}
          bodyStyle={{ padding: "24px" }}
          style={{ borderRadius: "12px", overflow: "hidden" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#212529" }}>Approve Verification Request</h2>
          </div>
          <p style={{ color: "#495057", fontSize: "14px", marginBottom: "16px" }}>
            Please provide a reason for approving this verification request. This will be recorded in the audit log.
          </p>
          <Input.TextArea
            rows={4}
            placeholder="Reason for approval (e.g., All documents verified, email domain matches, employment confirmed)..."
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            style={{
              background: "#f1f3f5",
              border: "none",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "14px",
              color: "#495057",
              resize: "none",
              outline: "none",
              marginBottom: "24px"
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <Button
              onClick={() => {
                setApproveModalVisible(false);
                setReviewNote("");
              }}
              style={{
                borderRadius: "8px",
                height: "40px",
                padding: "0 20px",
                fontWeight: 600,
                border: "1px solid #dee2e6"
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={submitting}
              onClick={handleApproveSubmit}
              style={{
                borderRadius: "8px",
                height: "40px",
                padding: "0 20px",
                fontWeight: 600,
                background: "#3b82f6",
                borderColor: "#3b82f6"
              }}
            >
              Approve Request
            </Button>
          </div>
        </Modal>
      </div>
    </Fragment>
  );
};

export default ViewVerification;
